import React, { useState, useEffect } from 'react';
import {
  Clock, TrendingUp, Utensils, DollarSign, Calendar,
  Search, Filter, Download, Eye, BarChart3, User,
  Heart, AlertTriangle, Star, Target, X
} from 'lucide-react';
import { staffService } from '../utils/supabaseStaffService';
import { logger } from '../utils/logger';

// Simple currency formatter
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const StaffOrderHistory = ({ currentUser }) => {
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staffAnalytics, setStaffAnalytics] = useState(null);

  useEffect(() => {
    loadStaffHistory();
  }, [currentUser]);

  const loadStaffHistory = async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const staff = await staffService.getStaffForPhysician(currentUser.id);
      const enrichedStaff = staff.map(staffMember => {
        const analytics = calculateStaffAnalytics(staffMember);
        return { ...staffMember, analytics };
      });

      setStaffMembers(enrichedStaff);

      // Calculate overall analytics
      const overallAnalytics = calculateOverallAnalytics(enrichedStaff);
      setStaffAnalytics(overallAnalytics);
    } catch (error) {
      logger.error('Error loading staff history:', error);
    }
    
    setLoading(false);
  };

  const calculateStaffAnalytics = (staffMember) => {
    const orderHistory = staffMember.order_history || [];
    
    if (orderHistory.length === 0) {
      return {
        totalOrders: 0,
        totalSpent: 0,
        averageOrderValue: 0,
        favoriteItems: [],
        lastOrderDate: null,
        orderFrequency: 0,
        costTrends: [],
        dietaryConsistency: 100
      };
    }

    const totalSpent = orderHistory.reduce((sum, order) => sum + (order.cost || 0), 0);
    const averageOrderValue = totalSpent / orderHistory.length;
    
    // Calculate favorite items
    const itemCounts = {};
    orderHistory.forEach(order => {
      (order.items || []).forEach(item => {
        const itemName = item.name || 'Unknown Item';
        itemCounts[itemName] = (itemCounts[itemName] || 0) + (item.quantity || 1);
      });
    });
    
    const favoriteItems = Object.entries(itemCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Calculate order frequency (orders per month)
    const firstOrderDate = new Date(orderHistory[0].orderedAt);
    const lastOrderDate = new Date(orderHistory[orderHistory.length - 1].orderedAt);
    const monthsDiff = Math.max(1, (lastOrderDate - firstOrderDate) / (1000 * 60 * 60 * 24 * 30));
    const orderFrequency = orderHistory.length / monthsDiff;

    // Calculate cost trends (last 10 orders)
    const recentOrders = orderHistory.slice(-10);
    const costTrends = recentOrders.map((order, index) => ({
      order: index + 1,
      cost: order.cost || 0,
      date: order.orderedAt
    }));

    // Calculate dietary consistency (how often they stick to their preferences)
    let consistentOrders = 0;
    const dietaryRestrictions = staffMember.dietary_restrictions || [];
    const allergens = staffMember.allergens || [];
    
    orderHistory.forEach(order => {
      // This is a simplified consistency check
      // In a real implementation, you'd check if ordered items align with dietary restrictions
      let isConsistent = true;
      
      // For demo purposes, assume 80-95% consistency
      const randomConsistency = Math.random();
      if (randomConsistency > 0.85) {
        isConsistent = false;
      }
      
      if (isConsistent) {
        consistentOrders++;
      }
    });
    
    const dietaryConsistency = orderHistory.length > 0 ? (consistentOrders / orderHistory.length) * 100 : 100;

    return {
      totalOrders: orderHistory.length,
      totalSpent,
      averageOrderValue,
      favoriteItems,
      lastOrderDate: lastOrderDate.toISOString(),
      orderFrequency,
      costTrends,
      dietaryConsistency
    };
  };

  const calculateOverallAnalytics = (staffList) => {
    const activeStaff = staffList.filter(s => s.isActive);
    
    if (activeStaff.length === 0) {
      return {
        totalStaff: 0,
        totalOrders: 0,
        totalSpent: 0,
        averageOrdersPerStaff: 0,
        averageSpentPerStaff: 0,
        mostActiveStaff: null,
        totalFavoriteItems: [],
        averageDietaryConsistency: 100
      };
    }

    const totalOrders = activeStaff.reduce((sum, staff) => sum + staff.analytics.totalOrders, 0);
    const totalSpent = activeStaff.reduce((sum, staff) => sum + staff.analytics.totalSpent, 0);
    const averageOrdersPerStaff = totalOrders / activeStaff.length;
    const averageSpentPerStaff = totalSpent / activeStaff.length;
    
    // Find most active staff member
    const mostActiveStaff = activeStaff.reduce((most, staff) => 
      staff.analytics.totalOrders > (most?.analytics?.totalOrders || 0) ? staff : most
    , null);

    // Aggregate favorite items across all staff
    const allFavoriteItems = {};
    activeStaff.forEach(staff => {
      staff.analytics.favoriteItems.forEach(item => {
        allFavoriteItems[item.name] = (allFavoriteItems[item.name] || 0) + item.count;
      });
    });
    
    const totalFavoriteItems = Object.entries(allFavoriteItems)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));

    // Calculate average dietary consistency
    const averageDietaryConsistency = activeStaff.reduce((sum, staff) => 
      sum + staff.analytics.dietaryConsistency, 0) / activeStaff.length;

    return {
      totalStaff: activeStaff.length,
      totalOrders,
      totalSpent,
      averageOrdersPerStaff,
      averageSpentPerStaff,
      mostActiveStaff,
      totalFavoriteItems,
      averageDietaryConsistency
    };
  };

  const filteredStaff = staffMembers.filter(staff => {
    if (!staff.isActive) return false;
    
    const matchesSearch = !searchTerm || 
      staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.role.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (timeFilter === 'all') return true;
    
    const now = new Date();
    const filterDate = new Date();
    
    switch (timeFilter) {
      case 'last-30-days':
        filterDate.setDate(now.getDate() - 30);
        break;
      case 'last-90-days':
        filterDate.setDate(now.getDate() - 90);
        break;
      case 'this-year':
        filterDate.setFullYear(now.getFullYear(), 0, 1);
        break;
      default:
        return true;
    }
    
    const hasRecentOrders = staff.analytics.lastOrderDate && 
      new Date(staff.analytics.lastOrderDate) >= filterDate;
    
    return hasRecentOrders;
  });

  const viewStaffDetails = (staff) => {
    setSelectedStaff(staff);
    setShowDetailsModal(true);
  };

  const exportStaffHistory = () => {
    const csvData = [
      ['Staff Name', 'Role', 'Total Orders', 'Total Spent', 'Average Order Value', 'Last Order Date', 'Favorite Items', 'Dietary Consistency'],
      ...filteredStaff.map(staff => [
        staff.name,
        staff.role,
        staff.analytics.totalOrders,
        formatCurrency(staff.analytics.totalSpent),
        formatCurrency(staff.analytics.averageOrderValue),
        staff.analytics.lastOrderDate ? new Date(staff.analytics.lastOrderDate).toLocaleDateString() : 'Never',
        staff.analytics.favoriteItems.map(item => `${item.name} (${item.count})`).join('; '),
        `${staff.analytics.dietaryConsistency.toFixed(1)}%`
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-order-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No User Data</h3>
          <p className="text-yellow-600">Unable to load staff order history. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Staff Order History & Preferences</h2>
            <p className="text-gray-600 mt-1">
              Track staff dining preferences and order patterns for better meeting planning
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={exportStaffHistory}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Overview */}
      {staffAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Staff</p>
                <p className="text-2xl font-bold text-gray-800">{staffAnalytics.totalStaff}</p>
              </div>
              <User className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-800">{staffAnalytics.totalOrders}</p>
              </div>
              <Utensils className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spent</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(staffAnalytics.totalSpent)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Consistency</p>
                <p className="text-2xl font-bold text-gray-800">{staffAnalytics.averageDietaryConsistency.toFixed(1)}%</p>
              </div>
              <Target className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search staff members..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="last-30-days">Last 30 Days</option>
            <option value="last-90-days">Last 90 Days</option>
            <option value="this-year">This Year</option>
          </select>
        </div>
      </div>

      {/* Popular Items Section */}
      {staffAnalytics && staffAnalytics.totalFavoriteItems.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Most Popular Items</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {staffAnalytics.totalFavoriteItems.slice(0, 5).map((item, index) => (
              <div key={item.name} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <Star className={`w-5 h-5 ${index < 3 ? 'text-yellow-500' : 'text-gray-400'}`} />
                  <span className="ml-1 text-sm font-medium">#{index + 1}</span>
                </div>
                <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                <p className="text-xs text-gray-600">{item.count} orders</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Staff List */}
      <div className="bg-white rounded-lg shadow-md">
        {filteredStaff.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Order History</h3>
            <p className="text-gray-600">
              {staffMembers.length === 0 
                ? 'Add staff members to start tracking their order history.'
                : 'Selected staff members have no order history yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Staff Member</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Orders</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Total Spent</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Avg Order</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Last Order</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Consistency</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStaff.map((staff) => (
                  <tr key={staff.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-800">{staff.name}</p>
                        <p className="text-sm text-gray-600">{staff.role}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Utensils className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{staff.analytics.totalOrders}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-800">
                        {formatCurrency(staff.analytics.totalSpent)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-gray-600">
                        {formatCurrency(staff.analytics.averageOrderValue)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-gray-600">
                        {staff.analytics.lastOrderDate 
                          ? new Date(staff.analytics.lastOrderDate).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-12 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 rounded-full h-2" 
                            style={{ width: `${staff.analytics.dietaryConsistency}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          {staff.analytics.dietaryConsistency.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => viewStaffDetails(staff)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Staff Details Modal */}
      {showDetailsModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedStaff.name} - Order History & Preferences
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Staff Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">Order Statistics</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Orders:</span>
                      <span className="font-medium">{selectedStaff.analytics.totalOrders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Spent:</span>
                      <span className="font-medium">{formatCurrency(selectedStaff.analytics.totalSpent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average Order:</span>
                      <span className="font-medium">{formatCurrency(selectedStaff.analytics.averageOrderValue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Frequency:</span>
                      <span className="font-medium">{selectedStaff.analytics.orderFrequency.toFixed(1)}/month</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">Dietary Preferences</h4>
                  <div className="space-y-2">
                    {selectedStaff.dietary_restrictions?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Dietary Restrictions:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedStaff.dietary_restrictions.map((restriction, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                              <Heart className="w-3 h-3 mr-1" />
                              {restriction}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedStaff.allergens?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Allergens:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedStaff.allergens.map((allergen, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {allergen}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">Favorite Items</h4>
                  <div className="space-y-1">
                    {selectedStaff.analytics.favoriteItems.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700 truncate">{item.name}</span>
                        <span className="text-gray-600 ml-2">{item.count}×</span>
                      </div>
                    ))}
                    {selectedStaff.analytics.favoriteItems.length === 0 && (
                      <p className="text-sm text-gray-500">No order history yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Cost Trends Chart */}
              {selectedStaff.analytics.costTrends.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">Recent Order Cost Trends</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-end space-x-2 h-32">
                      {selectedStaff.analytics.costTrends.map((trend, idx) => {
                        const maxCost = Math.max(...selectedStaff.analytics.costTrends.map(t => t.cost));
                        const height = maxCost > 0 ? (trend.cost / maxCost) * 100 : 0;
                        
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center">
                            <div className="text-xs text-gray-600 mb-1">
                              {formatCurrency(trend.cost)}
                            </div>
                            <div 
                              className="bg-blue-500 rounded-t w-full"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                            ></div>
                            <div className="text-xs text-gray-500 mt-1">#{trend.order}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Order History */}
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Recent Order History</h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {(selectedStaff.order_history || []).slice(-10).reverse().map((order, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {new Date(order.orderedAt).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            Meeting ID: {order.meetingId}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          {formatCurrency(order.cost)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {(order.items || []).map((item, itemIdx) => (
                          <span key={itemIdx}>
                            {item.name} {item.quantity && item.quantity > 1 && `(${item.quantity})`}
                            {itemIdx < order.items.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(!selectedStaff.order_history || selectedStaff.order_history.length === 0) && (
                    <p className="text-gray-500 text-center py-4">No order history available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffOrderHistory;